import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Calculator,
  FileText,
  IndianRupee,
  Globe,
  TrendingUp,
  Lightbulb,
  CreditCard,
  Mail,
  Sparkles,
  Clock,
  BarChart3,
  ShieldCheck,
  ChevronRight,
  Flame,
  PiggyBank,
  GraduationCap,
  Building2,
  Scale,
  Wallet,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import { LanguageGateway } from "@/components/LanguageGateway";
import { LanguageBottomSheet } from "@/components/LanguageBottomSheet";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { IndianPrideFooter } from "@/components/IndianPrideFooter";
import { formatIndianShort } from "@/lib/formatIndian";
import rupeeVikasLogo from "/rupeevikas-logo.png";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ResourcesGuides } from "@/components/home/ResourcesGuides";
import { AcademyBanner } from "@/components/home/AcademyBanner";

/* ─── Sticky Header (Glassmorphism) ─── */
function StickyHeader() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass-light shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16 md:h-20">
        <Link to="/" className="flex items-center gap-0">
          <img src={rupeeVikasLogo} alt="RupeeVikas" className="h-12 w-12 md:h-14 md:w-14 object-contain" />
          <div className="flex flex-col">
            <span className="text-2xl md:text-4xl text-foreground">
              <span className="font-bold text-amber-500">Rupee</span><span className="font-normal text-emerald-600">Vikas</span>
            </span>
            <span className="text-[8px] md:text-[10px] text-muted-foreground tracking-wide font-medium -mt-0.5">Plan, Track, and Grow Every Rupee</span>
          </div>
        </Link>

        <div className="flex items-center gap-3">
          <LanguageBottomSheet />
          <Link to="/auth">
            <Button size="sm" className="rounded-full px-5 text-xs font-semibold">
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ─── Hero Section with Future Wealth Slider ─── */
function HeroSection() {
  const { t, lang } = useLanguage();
  const [savings, setSavings] = useState(5000);

  const futureWealth = useMemo(() => {
    const r = 0.12 / 12;
    const n = 10 * 12;
    return Math.round(savings * ((Math.pow(1 + r, n) - 1) / r) * (1 + r));
  }, [savings]);

  const bubbleScale = Math.min(1 + (savings / 50000) * 1.5, 2.5);

  const formatAmount = (n: number) => formatIndianShort(n);

  return (
    <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full bg-primary/10 blur-[100px]" />
      <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-accent/10 blur-[80px]" />

      <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
        <p
          className="text-lg sm:text-xl text-muted-foreground font-medium italic animate-fade-up"
        >
          {t("hero.premiumHeadline")}
        </p>

        <h1
          className="mt-3 font-display text-[28px] sm:text-4xl md:text-[42px] font-bold tracking-tight text-foreground leading-[1.15] animate-fade-up [animation-delay:150ms]"
        >
          {t("hero.seoH1")}
        </h1>

        <p
          className="mt-4 text-base text-muted-foreground max-w-xl mx-auto leading-relaxed animate-fade-up [animation-delay:300ms]"
        >
          {t("hero.premiumSub")}
        </p>

        {/* Interactive Wealth Card */}
        <div
          className="mt-10 max-w-lg mx-auto rounded-2xl p-6 md:p-8 glass-light shadow-elevated animate-fade-up [animation-delay:350ms]"
        >
          <h3 className="font-display text-sm font-bold text-muted-foreground uppercase tracking-wider mb-6">
            {t("hero.wealthCardTitle")}
          </h3>

          {/* Animated Bubble */}
          <div className="flex justify-center mb-6">
            <div
              className="relative h-28 w-28 rounded-full bg-gradient-to-br from-primary/80 to-primary/40 flex items-center justify-center transition-transform duration-500 ease-out shadow-lg"
              style={{ transform: `scale(${bubbleScale})` }}
            >
              <div className="text-center">
              <p className="font-display text-sm font-bold leading-none text-emerald-300 drop-shadow-[0_0_8px_rgba(16,185,129,0.6)] transition-all duration-300">
                  {formatAmount(futureWealth)}
                </p>
                <p className="text-primary-foreground/70 text-[10px] mt-0.5">in 10 yrs</p>
              </div>
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-3">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("hero.monthlySavings")}</span>
              <span className="font-bold text-foreground">{formatAmount(savings)}/mo</span>
            </div>
            <Slider
              value={[savings]}
              onValueChange={([v]) => setSavings(v)}
              min={1000}
              max={100000}
              step={1000}
              className="flex-1"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>₹1K</span>
              <span>₹1L</span>
            </div>
          </div>

          {/* CTA */}
          <Link to="/auth" className="block mt-6">
            <Button
              size="lg"
              className="w-full gap-2 h-12 text-base font-bold rounded-xl animate-pulse-ring"
            >
              <Sparkles className="h-4 w-4" />
              {t("hero.premiumCta")}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── Tool Galaxy (Bento Grid) ─── */
const ALL_TOOLS = [
  { id: "wealth", icon: Calculator, link: "/calculators", cat: "invest" },
  { id: "stock", icon: TrendingUp, link: "/stock-research", cat: "invest" },
  { id: "multiAsset", icon: BarChart3, link: "/asset-comparison", cat: "invest" },
  { id: "realEstate", icon: Building2, link: "/real-estate", cat: "home" },
  { id: "rentBuy", icon: Scale, link: "/rent-vs-buy", cat: "home" },
  { id: "retirement", icon: Clock, link: "/retirement", cat: "invest" },
  { id: "taxSim", icon: FileText, link: "/tax-simulator", cat: "tax" },
  { id: "debt", icon: Flame, link: "/debt-killer", cat: "debt" },
  { id: "creditCard", icon: CreditCard, link: "/credit-card-master", cat: "tax" },
  { id: "childFuture", icon: GraduationCap, link: "/life-stages", cat: "kids" },
  { id: "canibuyit", icon: Wallet, link: "/emi-mentor", cat: "debt" },
  { id: "emergency", icon: Shield, link: "/calculators", cat: "invest" },
  { id: "inflation", icon: PiggyBank, link: "/calculators", cat: "invest" },
  
];

const CATEGORIES = [
  { id: "all", emoji: "✨" },
  { id: "debt", emoji: "⛓️" },
  { id: "invest", emoji: "📈" },
  { id: "home", emoji: "🏠" },
  { id: "kids", emoji: "🎓" },
  { id: "tax", emoji: "📝" },
];

function ToolGalaxy() {
  const { t } = useLanguage();
  const [activeCat, setActiveCat] = useState("all");

  const filteredTools = activeCat === "all"
    ? ALL_TOOLS
    : ALL_TOOLS.filter((tool) => tool.cat === activeCat);

  return (
    <section className="py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">
            {t("home.toolkitTitle")}
          </h2>
          <p className="mt-2 text-muted-foreground text-sm md:text-base max-w-xl mx-auto">
            {t("home.toolkitSub")}
          </p>
        </div>

        {/* Category Filter Chips */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8 scrollbar-hide -mx-4 px-4">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCat(cat.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 border ${
                activeCat === cat.id
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-card text-muted-foreground border-border hover:border-primary/40"
              }`}
            >
              {cat.emoji} {t(`cat.${cat.id}`)}
            </button>
          ))}
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {filteredTools.map((tool, i) => (
            <Link key={tool.id} to={tool.link} className="group">
              <div
                className="clay flex-card p-4 md:p-5 h-full hover:shadow-elevated transition-all duration-300 group-hover:-translate-y-1 animate-fade-up"
                style={{ animationDelay: `${i * 30}ms` }}
              >
                {/* 3D Icon Container */}
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                  <tool.icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                </div>
                <h4 className="font-display text-sm md:text-base font-bold text-foreground mb-1 indic-text">
                  {t(`tool.${tool.id}.title`)}
                </h4>
                <p className="text-[11px] md:text-xs text-muted-foreground leading-relaxed indic-text">
                  {t(`tool.${tool.id}.micro`)}
                </p>
                <div className="mt-3 flex items-center gap-0.5 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  {t("grid.openTool")} <ChevronRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Trust Stats ─── */
function TrustStats() {
  const { t } = useLanguage();
  const stats = [
    { value: "14+", label: t("home.statTools") },
    { value: "13", label: t("home.statLangs") },
    { value: "₹0", label: t("home.statFree") },
    { value: "100%", label: t("home.statPrivacy") },
  ];

  return (
    <section className="py-10 bg-primary text-primary-foreground">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display text-2xl md:text-3xl font-bold">{s.value}</div>
              <div className="text-xs mt-1 opacity-80">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Why RupeeVikas ─── */
function WhyRupeeVikas() {
  const { t } = useLanguage();
  const reasons = [
    { icon: ShieldCheck, title: t("home.whyFreeTitle"), desc: t("home.whyFreeDesc") },
    { icon: Globe, title: t("home.whyLangTitle"), desc: t("home.whyLangDesc") },
    { icon: IndianRupee, title: t("home.whyIndiaTitle"), desc: t("home.whyIndiaDesc") },
    { icon: Lightbulb, title: t("home.whySathiTitle"), desc: t("home.whySathiDesc") },
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">
            {t("home.whyTitle")}
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {reasons.map((r) => (
            <div key={r.title} className="clay p-5 text-center space-y-2">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 mx-auto">
                <r.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-display text-sm font-bold text-foreground">{r.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Contact Section ─── */
function ContactSection() {
  const { t, lang } = useLanguage();

  const contactPrompts: Record<string, string> = {
    en: "You can write to us in English at",
    hi: "आप हमें हिंदी में लिख सकते हैं",
    kn: "ನೀವು ಕನ್ನಡದಲ್ಲಿ ನಮಗೆ ಬರೆಯಬಹುದು",
    te: "మీరు తెలుగులో మాకు రాయవచ్చు",
    ta: "நீங்கள் தமிழில் எங்களுக்கு எழுதலாம்",
    mr: "तुम्ही आम्हाला मराठीत लिहू शकता",
    gu: "તમે અમને ગુજરાતીમાં લખી શકો છો",
    ml: "നിങ്ങൾ മലയാളത്തിൽ ഞങ്ങൾക്ക് എഴുതാം",
    bn: "আপনি বাংলায় আমাদের লিখতে পারেন",
    pa: "ਤੁਸੀਂ ਸਾਨੂੰ ਪੰਜਾਬੀ ਵਿੱਚ ਲਿਖ ਸਕਦੇ ਹੋ",
    or: "ଆପଣ ଓଡ଼ିଆରେ ଆମକୁ ଲେଖିପାରିବେ",
    as: "আপুনি অসমীয়াত আমালৈ লিখিব পাৰে",
    ur: "آپ ہمیں اردو میں لکھ سکتے ہیں",
  };

  return (
    <section className="py-16 md:py-20">
      <div className="max-w-lg mx-auto px-4 md:px-8 text-center">
        <div className="clay p-8 md:p-10">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/20 mb-4">
            <Mail className="h-7 w-7 text-accent" />
          </div>
          <h2 className="font-display text-xl md:text-2xl font-bold text-foreground mb-2 indic-text">
            {t("contact.title")}
          </h2>
          <p className="text-sm text-muted-foreground mb-3 leading-relaxed indic-text">
            {t("contact.desc")}
          </p>
          <p className="text-xs text-muted-foreground mb-5 indic-text">
            {contactPrompts[lang] || contactPrompts.en}
          </p>
          <a
            href="mailto:help@rupeevikas.in"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity animate-pulse-ring"
          >
            <Mail className="h-4 w-4" />
            help@rupeevikas.in
          </a>
          <p className="mt-6 text-[10px] text-muted-foreground/60">© 2026 RupeeVikas. All Rights Reserved.</p>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="border-t border-border bg-muted/20 py-10">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center overflow-hidden">
              <img src={rupeeVikasLogo} alt="RupeeVikas" className="h-7 w-7 object-contain" />
            </div>
            <span className="text-foreground">
              <span className="font-bold text-amber-500">Rupee</span><span className="font-normal text-emerald-600">Vikas</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/legal-disclaimers" className="hover:text-foreground transition-colors">
              {t("footer.legal")}
            </Link>
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms-of-use" className="hover:text-foreground transition-colors">
              {t("footer.terms")}
            </Link>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-border text-center space-y-2">
          <p className="text-[11px] text-muted-foreground/70 leading-relaxed max-w-2xl mx-auto">
            {t("home.footerDisclaimer2")}
          </p>
          <p className="text-[11px] text-muted-foreground/50">
            {t("home.footerIndian")}
          </p>
          <IndianPrideFooter />
        </div>
      </div>
    </footer>
  );
}

/* ─── Page ─── */
export default function HomePage() {
  usePageTitle();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  if (user) return null;

  return (
    <div className="min-h-screen bg-background" lang={lang}>
      <LanguageGateway />
      <StickyHeader />
      <HeroSection />
      <AcademyBanner />
      <ToolGalaxy />
      <ResourcesGuides />
      <TrustStats />
      <WhyRupeeVikas />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
